import { ref, computed, readonly, type Ref, type ComputedRef } from 'vue';
import type { AuthUser, LoginCredentials, SignupCredentials, AuthError } from '@/types/auth';
import {
  isAuthError,
  isAuthUser,
  createAuthError,
  isAuthErrorBody,
  toAuthErrorType,
} from '@/types/auth';
import { AUTH_CONFIG, ENV_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import { safeIdentify, safeClear } from '@/telemetry/openpanel';
import { randomUuid } from '@/utils/uuid';

// Prevent mock auth from shipping in production builds.
// Uses MODE (not PROD) because `vite build --mode development` sets PROD=true
// but MODE='development', and integration tests depend on that build mode.
const useMock = AUTH_CONFIG.isMockAuthEnabled();
if (import.meta.env.MODE === 'production' && useMock) {
  throw new Error('Mock auth must not be enabled in production builds');
}

// Module-level singleton state (same pattern as webSocketSingleton.ts)
const authUser = ref<AuthUser | null>(null);
const isLoading = ref(false);
const authError = ref<AuthError | null>(null);
const rateLimitCountdown = ref(0);
const authCheckComplete = ref(false);

let rateLimitTimer: ReturnType<typeof setInterval> | null = null;

// --- Auth API interface ---

interface AuthApi {
  login(credentials: LoginCredentials): Promise<AuthUser>;
  signup(credentials: SignupCredentials): Promise<AuthUser>;
  checkSession(): Promise<AuthUser | null>;
  logout(): Promise<void>;
  reset?(): void;
}

// --- Mock implementation ---

const MOCK_STORAGE_KEY = 'lexicontest_mock_auth_session';

const mockUserStore = new Map<string, { user: AuthUser; email: string; password: string }>();
let mockFailureCount = 0;

function persistMockSession(user: AuthUser): void {
  try {
    localStorage.setItem(MOCK_STORAGE_KEY, JSON.stringify(user));
  } catch (e: unknown) {
    console.warn('[auth] Failed to persist mock session:', e);
  }
}

function clearMockSession(): void {
  try {
    localStorage.removeItem(MOCK_STORAGE_KEY);
  } catch (e: unknown) {
    console.warn('[auth] Failed to clear mock session:', e);
  }
}

function loadMockSession(): AuthUser | null {
  try {
    const stored = localStorage.getItem(MOCK_STORAGE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (isAuthUser(parsed)) {
        return parsed;
      }
      // Corrupted or wrong-shape data; clear it
      console.warn('[auth] Invalid mock session shape in localStorage, clearing');
      localStorage.removeItem(MOCK_STORAGE_KEY);
    }
  } catch (e: unknown) {
    console.warn('[auth] Failed to load mock session, clearing:', e);
    try {
      localStorage.removeItem(MOCK_STORAGE_KEY);
    } catch {
      // localStorage entirely unavailable
    }
  }
  return null;
}

let mockResendCount = 0;
let mockLastResendTime = 0;

async function mockDelay(): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, AUTH_CONFIG.MOCK_API_DELAY));
}

const mockAuthApi = {
  async login(credentials: LoginCredentials): Promise<AuthUser> {
    await mockDelay();

    // Rate limit after 5 consecutive failures
    if (mockFailureCount >= 5) {
      throw createAuthError('rate_limited', STRINGS.auth.errors.rateLimited, 30);
    }

    const entry = [...mockUserStore.values()].find(e => e.email === credentials.email);

    if (!entry || entry.password !== credentials.password) {
      mockFailureCount++;
      throw createAuthError('invalid_credentials', STRINGS.auth.errors.invalidCredentials);
    }

    mockFailureCount = 0;
    persistMockSession(entry.user);
    return entry.user;
  },

  async signup(credentials: SignupCredentials): Promise<AuthUser> {
    await mockDelay();

    const emailTaken = [...mockUserStore.values()].some(e => e.email === credentials.email);
    if (emailTaken) {
      throw createAuthError('email_taken', STRINGS.auth.errors.emailTaken);
    }

    const user: AuthUser = {
      id: randomUuid(),
      name: credentials.name,
      emailVerified: false,
    };

    mockUserStore.set(user.id, { user, email: credentials.email, password: credentials.password });
    mockFailureCount = 0;
    persistMockSession(user);
    return user;
  },

  async checkSession(): Promise<AuthUser | null> {
    await mockDelay();
    return loadMockSession();
  },

  async logout(): Promise<void> {
    clearMockSession();
  },

  reset(): void {
    if (import.meta.env.MODE === 'production') return;
    mockUserStore.clear();
    mockFailureCount = 0;
    mockResendCount = 0;
    mockLastResendTime = 0;
    authUser.value = null;
    isLoading.value = false;
    authError.value = null;
    authCheckComplete.value = false;
    rateLimitCountdown.value = 0;
    clearMockSession();
    if (rateLimitTimer !== null) {
      clearInterval(rateLimitTimer);
      rateLimitTimer = null;
    }
  },

  async devLogin(): Promise<AuthUser> {
    const devEmail = 'dev@dev';
    const existing = [...mockUserStore.values()].find(e => e.email === devEmail);
    if (existing) {
      authUser.value = existing.user;
      persistMockSession(existing.user);
      return existing.user;
    }

    const user: AuthUser = {
      id: randomUuid(),
      name: 'DevUser',
      emailVerified: true,
    };
    mockUserStore.set(user.id, { user, email: devEmail, password: '' });
    authUser.value = user;
    persistMockSession(user);
    return user;
  },
};

// --- Real implementation ---

function parseRetryAfter(raw: string | null): number {
  const parsed = parseInt(raw ?? '30', 10);
  // Safe default: 30s is a reasonable rate-limit cooldown when the header is garbage
  return Number.isNaN(parsed) ? 30 : parsed;
}

function parseAndValidateUser(json: unknown): AuthUser {
  if (isAuthUser(json)) {
    return json;
  }
  throw createAuthError('unknown', 'Server returned an invalid response');
}

const realAuthApi: AuthApi = {
  async login(credentials: LoginCredentials): Promise<AuthUser> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(credentials),
    });

    if (response.status === 429) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      throw createAuthError('rate_limited', STRINGS.auth.errors.rateLimited, retryAfter);
    }

    if (response.status === 401) {
      throw createAuthError('invalid_credentials', STRINGS.auth.errors.invalidCredentials);
    }

    if (!response.ok) {
      throw createAuthError('unknown', STRINGS.auth.errors.unknown);
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }
    return parseAndValidateUser(json);
  },

  async signup(credentials: SignupCredentials): Promise<AuthUser> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(credentials),
    });

    if (response.status === 409) {
      let errType: ReturnType<typeof toAuthErrorType> = 'unknown';
      try {
        const json: unknown = await response.json();
        if (isAuthErrorBody(json)) {
          errType = toAuthErrorType(json.code);
        }
      } catch {
        // Body unreadable — fall through to unknown
      }
      if (errType === 'name_taken') {
        throw createAuthError('name_taken', STRINGS.auth.errors.nameTaken);
      }
      if (errType === 'email_taken') {
        throw createAuthError('email_taken', STRINGS.auth.errors.emailTaken);
      }
      throw createAuthError('unknown', STRINGS.auth.errors.unknown);
    }

    if (response.status === 429) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      throw createAuthError('rate_limited', STRINGS.auth.errors.rateLimited, retryAfter);
    }

    if (!response.ok) {
      throw createAuthError('unknown', STRINGS.auth.errors.unknown);
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }
    return parseAndValidateUser(json);
  },

  async checkSession(): Promise<AuthUser | null> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/me`, {
      credentials: 'include',
    });

    if (response.status === 401) {
      return null;
    }

    if (!response.ok) {
      throw createAuthError('unknown', `Session check failed with status ${response.status}`);
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }
    return parseAndValidateUser(json);
  },

  async logout(): Promise<void> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      console.warn(`[auth] Logout request failed with status ${response.status}`);
    }
  },
};

// --- API selection ---

const api: AuthApi = useMock ? mockAuthApi : realAuthApi;

// --- Shared helpers ---

function startRateLimitCountdown(seconds: number): void {
  rateLimitCountdown.value = seconds;
  if (rateLimitTimer !== null) {
    clearInterval(rateLimitTimer);
  }
  rateLimitTimer = setInterval(() => {
    rateLimitCountdown.value--;
    if (rateLimitCountdown.value <= 0) {
      if (rateLimitTimer !== null) {
        clearInterval(rateLimitTimer);
        rateLimitTimer = null;
      }
      rateLimitCountdown.value = 0;
    }
  }, 1000);
}

/**
 * Updates the module-level authUser ref directly. Used by sibling composables
 * (e.g., useProfile) that receive a new JWT after a profile mutation.
 */
export function _updateAuthUser(user: AuthUser): void {
  authUser.value = user;
}

/**
 * Clears auth state without making a network request.
 * Used after account deletion where the server session is already invalidated.
 */
export function _clearAuthState(): void {
  authUser.value = null;
  authError.value = null;
  safeClear();
}

/**
 * Mock-only: returns the email stored for a user in the in-memory mock store.
 * Email is no longer part of {@link AuthUser}; the mock profile API uses this to
 * back its `fetchAccountEmail` so dev mock mode surfaces the signed-up address.
 */
export function _getMockUserEmail(id: string): string | undefined {
  return mockUserStore.get(id)?.email;
}

async function mockResendVerification(): Promise<void> {
  await mockDelay();

  const now = Date.now();
  // 5-minute cooldown (5000ms in mock for testability)
  if (now - mockLastResendTime < 5000) {
    throw createAuthError('rate_limited', 'Please wait before requesting another email.', 300);
  }

  mockResendCount++;
  if (mockResendCount > 10) {
    throw createAuthError(
      'account_locked',
      'Account locked due to excessive verification requests.'
    );
  }

  mockLastResendTime = now;
}

async function realResendVerification(): Promise<void> {
  const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/resend-verification`, {
    method: 'POST',
    credentials: 'include',
  });

  if (response.status === 429) {
    const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
    startRateLimitCountdown(retryAfter);
    throw createAuthError(
      'rate_limited',
      'Please wait before requesting another email.',
      retryAfter
    );
  }

  if (response.status === 409) {
    throw createAuthError('already_verified', 'Email is already verified.');
  }

  if (response.status === 403) {
    throw createAuthError(
      'account_locked',
      'Account locked due to excessive verification requests.'
    );
  }

  if (response.status === 401) {
    throw createAuthError('unknown', 'Session expired. Please log in again.');
  }

  if (!response.ok) {
    throw createAuthError('unknown', 'Failed to resend verification email. Please try again.');
  }
}

// --- Public composable ---

export function useAuth() {
  async function login(credentials: LoginCredentials): Promise<void> {
    isLoading.value = true;
    authError.value = null;

    try {
      const user = await api.login(credentials);
      authUser.value = user;
      safeIdentify({ profileId: user.id, firstName: user.name });
    } catch (err: unknown) {
      if (isAuthError(err)) {
        if (err.type === 'rate_limited' && err.retryAfterSeconds) {
          startRateLimitCountdown(err.retryAfterSeconds);
        }
        authError.value = err;
      } else {
        authError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw authError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function signup(credentials: SignupCredentials): Promise<void> {
    if (credentials.password.length < AUTH_CONFIG.PASSWORD_MIN_LENGTH) {
      const err = createAuthError(
        'unknown',
        `Password must be at least ${AUTH_CONFIG.PASSWORD_MIN_LENGTH} characters`
      );
      authError.value = err;
      throw err;
    }

    isLoading.value = true;
    authError.value = null;

    try {
      const user = await api.signup(credentials);
      authUser.value = user;
      safeIdentify({ profileId: user.id, firstName: user.name });
    } catch (err: unknown) {
      if (isAuthError(err)) {
        if (err.type === 'rate_limited' && err.retryAfterSeconds) {
          startRateLimitCountdown(err.retryAfterSeconds);
        }
        authError.value = err;
      } else {
        authError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw authError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } catch (err: unknown) {
      console.warn('[auth] Logout request failed (clearing local state anyway):', err);
    }
    authUser.value = null;
    safeClear();
    authError.value = null;
  }

  async function checkSession(): Promise<void> {
    isLoading.value = true;
    try {
      const user = await api.checkSession();
      authUser.value = user;
      if (user) {
        safeIdentify({ profileId: user.id, firstName: user.name });
      }
    } catch (err: unknown) {
      reportClientError(err, ErrorLocation.AuthCheckSessionFailed);
      // On server error, preserve existing session if we already have one
    } finally {
      isLoading.value = false;
      authCheckComplete.value = true;
    }
  }

  async function verifyEmail(token: string): Promise<void> {
    isLoading.value = true;
    authError.value = null;

    try {
      const response = await fetch(
        `${ENV_CONFIG.getApiBaseUrl()}/api/auth/verify-email?token=${encodeURIComponent(token)}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const text = await response.text().catch(() => 'Verification failed');
        throw createAuthError('unknown', text);
      }

      let json: unknown;
      try {
        json = await response.json();
      } catch {
        throw createAuthError('unknown', 'Server returned an invalid response');
      }
      const user = parseAndValidateUser(json);
      authUser.value = user;
    } catch (err: unknown) {
      if (isAuthError(err)) {
        authError.value = err;
      } else {
        authError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw authError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function resendVerification(): Promise<void> {
    authError.value = null;

    try {
      if (useMock) {
        await mockResendVerification();
      } else {
        await realResendVerification();
      }
    } catch (err: unknown) {
      if (isAuthError(err)) {
        // If already verified, refresh auth state
        if (err.type === 'already_verified' && authUser.value) {
          authUser.value = { ...authUser.value, emailVerified: true };
        }
        authError.value = err;
      } else {
        authError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw authError.value;
    }
  }

  function clearError(): void {
    authError.value = null;
  }

  return {
    user: readonly(authUser) as Readonly<Ref<AuthUser | null>>,
    isLoading: readonly(isLoading) as Readonly<Ref<boolean>>,
    error: readonly(authError) as Readonly<Ref<AuthError | null>>,
    rateLimitCountdown: readonly(rateLimitCountdown) as Readonly<Ref<number>>,
    authCheckComplete: readonly(authCheckComplete) as Readonly<Ref<boolean>>,
    isAuthenticated: computed(() => authUser.value !== null) as ComputedRef<boolean>,
    isEmailVerified: computed(() => authUser.value?.emailVerified ?? false) as ComputedRef<boolean>,
    login,
    signup,
    logout,
    checkSession,
    verifyEmail,
    resendVerification,
    clearError,
    // Exposed for testing only
    _resetMockState: api.reset ?? (() => {}),
    _setMockUser: (user: AuthUser | null) => {
      authUser.value = user;
    },
    _setMockAuthCheckComplete: (value: boolean) => {
      authCheckComplete.value = value;
    },
    // Dev-only bypass (only present when useMock is true)
    ...(useMock ? { devLogin: mockAuthApi.devLogin } : {}),
  };
}
